import { z } from "zod";

/**
 * Bump this whenever the envelope's own shape changes (not when a ruleset's
 * inner `data` shape changes -- rulesets carry their own version string).
 * Every bump needs a matching entry in `migrations` below.
 */
export const CURRENT_SCHEMA_VERSION = 1;

export const characterKindSchema = z.enum(["captain", "pilot"]);
export type CharacterKind = z.infer<typeof characterKindSchema>;

/**
 * The unit of persistence and of export. Everything the app can save, load,
 * or hand to a future API is wrapped in one of these.
 *
 * `data` is deliberately opaque here: core knows nothing about what a Captain
 * is. The owning ruleset validates it.
 */
export const characterEnvelopeSchema = z.object({
  schemaVersion: z.number().int().positive(),
  kind: characterKindSchema,
  /** Client-generated UUID, stable from creation through any future sync. */
  id: z.uuid(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  /** Which ruleset produced this, e.g. "leviathan". */
  rulesetId: z.string().min(1),
  /** Ruleset version at time of authoring, for rules-drift migration. */
  rulesetVersion: z.string().min(1),
  data: z.unknown(),
});

export type CharacterEnvelope<TData = unknown> = Omit<
  z.infer<typeof characterEnvelopeSchema>,
  "data"
> & { data: TData };

export interface CreateEnvelopeInput<TData> {
  kind: CharacterKind;
  rulesetId: string;
  rulesetVersion: string;
  data: TData;
  /** Override for tests; defaults to a fresh UUID. */
  id?: string;
  /** Override for tests; defaults to now. */
  now?: Date;
}

export function createEnvelope<TData>(
  input: CreateEnvelopeInput<TData>,
): CharacterEnvelope<TData> {
  const timestamp = (input.now ?? new Date()).toISOString();
  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    kind: input.kind,
    id: input.id ?? crypto.randomUUID(),
    createdAt: timestamp,
    updatedAt: timestamp,
    rulesetId: input.rulesetId,
    rulesetVersion: input.rulesetVersion,
    data: input.data,
  };
}

export function touchEnvelope<TData>(
  envelope: CharacterEnvelope<TData>,
  data: TData,
  now: Date = new Date(),
): CharacterEnvelope<TData> {
  return { ...envelope, data, updatedAt: now.toISOString() };
}

/**
 * Migrations from one schema version to the next. Key `n` upgrades a v`n`
 * envelope to v`n+1`. Kept as plain object transforms so they stay testable
 * against fixtures of genuinely old exports.
 */
const migrations: Record<number, (input: Record<string, unknown>) => Record<string, unknown>> = {
  // 1: (old) => ({ ...old, schemaVersion: 2, /* ... */ }),
};

export class EnvelopeParseError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = "EnvelopeParseError";
  }
}

/**
 * Parse an untrusted envelope (a pasted file, a localStorage entry written by
 * an older build) and bring it up to the current schema version.
 *
 * Does NOT validate `data` -- hand the result to the owning ruleset for that.
 */
export function parseEnvelope(raw: unknown): CharacterEnvelope {
  if (typeof raw !== "object" || raw === null) {
    throw new EnvelopeParseError("Expected an object.");
  }

  let working = raw as Record<string, unknown>;
  const declared = working.schemaVersion;

  if (typeof declared !== "number" || !Number.isInteger(declared) || declared < 1) {
    throw new EnvelopeParseError("Missing or invalid schemaVersion.");
  }

  if (declared > CURRENT_SCHEMA_VERSION) {
    throw new EnvelopeParseError(
      `This character was made with a newer version of the app (format v${declared}, this build reads up to v${CURRENT_SCHEMA_VERSION}).`,
    );
  }

  for (let version = declared; version < CURRENT_SCHEMA_VERSION; version++) {
    const migrate = migrations[version];
    if (!migrate) {
      throw new EnvelopeParseError(`No migration registered from format v${version}.`);
    }
    working = migrate(working);
  }

  const result = characterEnvelopeSchema.safeParse(working);
  if (!result.success) {
    throw new EnvelopeParseError("Envelope failed validation.", { cause: result.error });
  }
  return result.data as CharacterEnvelope;
}

/** Stable, pretty-printed JSON suitable for a downloadable .json export. */
export function serializeEnvelope(envelope: CharacterEnvelope<unknown>): string {
  return `${JSON.stringify(envelope, null, 2)}\n`;
}
